import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, MoreHorizontal, FileText, Trash2, CheckCircle2, Lock, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { BRL, STAGES, PIPELINE_STAGES, HISTORY_STAGES, normalizeStage, stageInfo, fmtData } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const emptyOp = {
  titulo: "", empresa: "", contato: "", email: "", whatsapp: "", valor: "", origem: "", servico: "",
  responsavel: "", data_fechamento: "", recorrente: false, notas: "", stage: "qualificar", decisor: "",
  necessidade: "", proxima_acao: "", data_proxima_acao: "", ultima_interacao: "", evidencia: "",
  situacao: "em_andamento", confianca_forecast: "aberta", data_retomada: "", motivo_perda: "",
};
const etapasAbertas = new Set(PIPELINE_STAGES.map((stage) => stage.key));
const confiancaForecast = {
  aberta: { label: "Aberta", classe: "text-[#A1A1AA] border-[#3F3F46] bg-[#27272A]/50" },
  provavel: { label: "Provável", classe: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10" },
  comprometida: { label: "Comprometida", classe: "text-[#8CC63F] border-[#8CC63F]/30 bg-[#8CC63F]/10" },
};
const statusProposta = {
  enviada: { label: "aguardando decisão", classe: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10" },
  aprovada: { label: "aprovada", classe: "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10" },
  recusada: { label: "recusada", classe: "text-[#F43F5E] border-[#F43F5E]/30 bg-[#F43F5E]/10" },
  substituida: { label: "versão substituída", classe: "text-[#71717A] border-[#71717A]/30 bg-[#71717A]/10" },
  encerrada: { label: "negócio encerrado", classe: "text-[#A1A1AA] border-[#71717A]/30 bg-[#71717A]/10" },
  arquivada: { label: "arquivada", classe: "text-[#71717A] border-[#71717A]/30 bg-[#71717A]/10" },
  sem_oportunidade: { label: "sem oportunidade", classe: "text-[#F43F5E] border-[#F43F5E]/30 bg-[#F43F5E]/10" },
};

const dataLocal = () => {
  const agora = new Date();
  agora.setMinutes(agora.getMinutes() - agora.getTimezoneOffset());
  return agora.toISOString().slice(0, 10);
};

const dataMaisDias = (dias) => {
  const data = new Date();
  data.setDate(data.getDate() + dias);
  data.setMinutes(data.getMinutes() - data.getTimezoneOffset());
  return data.toISOString().slice(0, 10);
};

const etapaDaOportunidade = (op) => normalizeStage(op.stage);
const oportunidadeAtiva = (op) => etapasAbertas.has(etapaDaOportunidade(op));
const dataDeMovimento = (op) => (op.ultima_interacao || op.updated_at || op.created_at || "").slice(0, 10);
const diasDesde = (valor) => {
  if (!valor) return null;
  const inicio = new Date(`${valor.slice(0, 10)}T12:00:00`);
  const hoje = new Date(`${dataLocal()}T12:00:00`);
  return Math.max(0, Math.floor((hoje - inicio) / 86400000));
};

const situacaoDaOportunidade = (op) => {
  const etapa = etapaDaOportunidade(op);
  if (etapa === "ganho") return { label: "Ganho", classe: "text-[#8CC63F] border-[#8CC63F]/30 bg-[#8CC63F]/10" };
  if (etapa === "perdido") return { label: "Perdido", classe: "text-[#71717A] border-[#71717A]/30 bg-[#71717A]/10" };
  if (etapa === "nutricao") return { label: "Pausada", classe: "text-[#8B5CF6] border-[#8B5CF6]/30 bg-[#8B5CF6]/10" };
  if (!op.proxima_acao || !op.data_proxima_acao) return { label: "Sem próximo passo", classe: "text-[#F43F5E] border-[#F43F5E]/30 bg-[#F43F5E]/10" };
  if (op.data_proxima_acao < dataLocal()) return { label: "Atrasada", classe: "text-[#F43F5E] border-[#F43F5E]/30 bg-[#F43F5E]/10" };
  if (op.data_proxima_acao === dataLocal()) return { label: "Hoje", classe: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10" };
  if (op.situacao === "aguardando_cliente") return { label: "Aguardando cliente", classe: "text-[#06B6D4] border-[#06B6D4]/30 bg-[#06B6D4]/10" };
  return { label: "Em andamento", classe: "text-[#8CC63F] border-[#8CC63F]/30 bg-[#8CC63F]/10" };
};

const formaPagamentoValida = (valor) => {
  const normalizado = String(valor || "").toLowerCase();
  return ["pix", "boleto", "cartao", "transferencia", "dinheiro", "outro"].includes(normalizado) ? normalizado : "boleto";
};

const formularioFechamento = (op, proposta = null) => {
  const recorrente = Boolean(op.recorrente);
  const valor = Number(proposta?.total ?? op.valor ?? 0);
  const hoje = dataLocal();
  return {
    cliente_id: "novo",
    empresa: op.empresa || "",
    contato: op.contato || "",
    email: op.email || "",
    whatsapp: op.whatsapp || "",
    tipo_contrato: recorrente ? "recorrente" : "pontual",
    formato: recorrente ? "mensal" : "pontual",
    tipo_projeto: op.servico || op.titulo || "",
    template_id: "auto",
    valor_total: recorrente ? "" : valor,
    valor_mensal: recorrente ? valor : "",
    pagamento_tipo: "a_vista",
    quantidade_parcelas: 1,
    frequencia_cobranca: "mensal",
    parcelas_por_mes: 4,
    primeiro_vencimento: hoje,
    forma_pagamento: formaPagamentoValida(proposta?.forma_pagamento),
    inicio: hoje,
    fim: "",
    nf: "netzer",
    renovacao_automatica: false,
    frequencia_captacao: "mensal",
    proposta_id: proposta?.id || "",
    parcelas_personalizadas: [],
  };
};

function FechamentoDialog({ contexto, form, setForm, clientes, templates, salvando, onClose, onSubmit }) {
  if (!contexto || !form) return null;
  const recorrente = form.tipo_contrato === "recorrente";
  const parcelado = form.pagamento_tipo === "parcelado";
  const personalizado = form.pagamento_tipo === "personalizado";
  const atualizarParcelaPersonalizada = (indice, campo, valor) => {
    const parcelas = [...(form.parcelas_personalizadas || [])];
    parcelas[indice] = { ...parcelas[indice], [campo]: valor };
    const total = parcelas.reduce((soma, parcela) => soma + Number(parcela.valor || 0), 0);
    setForm({ ...form, parcelas_personalizadas: parcelas, valor_total: total || "" });
  };
  const adicionarParcelaPersonalizada = () => {
    setForm({ ...form, parcelas_personalizadas: [...(form.parcelas_personalizadas || []), { data_vencimento: "", valor: "" }] });
  };
  const removerParcelaPersonalizada = (indice) => {
    const parcelas = (form.parcelas_personalizadas || []).filter((_, posicao) => posicao !== indice);
    const total = parcelas.reduce((soma, parcela) => soma + Number(parcela.valor || 0), 0);
    setForm({ ...form, parcelas_personalizadas: parcelas, valor_total: total || "" });
  };
  const selecionarCliente = (clienteId) => {
    if (clienteId === "novo") {
      setForm({ ...form, cliente_id: "novo" });
      return;
    }
    const cliente = clientes.find((item) => item.id === clienteId);
    setForm({
      ...form,
      cliente_id: clienteId,
      empresa: cliente?.empresa || form.empresa,
      contato: cliente?.nome || form.contato,
      email: cliente?.email || "",
      whatsapp: cliente?.whatsapp || "",
    });
  };
  const campo = "mt-1.5 bg-[#18181C] border-[#27272A]";
  const conteudoSelect = "bg-[#18181C] border-[#27272A] text-[#E4E4E0]";

  return (
    <Dialog open onOpenChange={(aberto) => { if (!aberto) onClose(); }}>
      <DialogContent className="bg-[#141418] border-[#8CC63F]/40 text-[#F4F4F0] max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl sm:text-3xl">Confirmar <span className="gold-italic">negócio fechado</span></DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#A1A1AA]">Preencha a ficha uma vez. Ela criará o contrato, as cobranças parceladas, o cliente e o primeiro ciclo de produção.</p>
        <form onSubmit={onSubmit} className="space-y-6 mt-2" data-testid="fechamento-form">
          <section className="studio-card p-4 space-y-4">
            <div>
              <p className="caption-label text-[#8CC63F]">1. Cadastro do cliente</p>
              <p className="text-xs text-[#71717A] mt-1">Use um cadastro existente ou crie o cliente durante o fechamento.</p>
            </div>
            <div>
              <Label className="caption-label">Cliente</Label>
              <Select value={form.cliente_id || "novo"} onValueChange={selecionarCliente}>
                <SelectTrigger data-testid="fechamento-cliente-select" className={campo}><SelectValue /></SelectTrigger>
                <SelectContent className={conteudoSelect}>
                  <SelectItem value="novo">+ Criar novo cliente</SelectItem>
                  {clientes.map((cliente) => <SelectItem key={cliente.id} value={cliente.id}>{cliente.empresa}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Empresa</Label><Input required data-testid="fechamento-empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} className={campo} /></div>
              <div><Label className="caption-label">Contato</Label><Input data-testid="fechamento-contato" value={form.contato} onChange={(e) => setForm({ ...form, contato: e.target.value })} className={campo} /></div>
              <div><Label className="caption-label">E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={campo} /></div>
              <div><Label className="caption-label">WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={campo} /></div>
            </div>
          </section>

          <section className="studio-card p-4 space-y-4">
            <div>
              <p className="caption-label text-[#06B6D4]">2. Contrato e operação</p>
              <p className="text-xs text-[#71717A] mt-1">O tipo do projeto e o template definem a estrutura inicial da produção.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Tipo de contrato</Label>
                <Select value={form.tipo_contrato} onValueChange={(valor) => setForm({ ...form, tipo_contrato: valor, formato: valor === "recorrente" ? "mensal" : "pontual" })}>
                  <SelectTrigger data-testid="fechamento-tipo-contrato" className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="pontual">Projeto pontual</SelectItem><SelectItem value="recorrente">Contrato recorrente</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="caption-label">Tipo do projeto / serviço</Label><Input required data-testid="fechamento-tipo-projeto" value={form.tipo_projeto} onChange={(e) => setForm({ ...form, tipo_projeto: e.target.value })} className={campo} /></div>
              <div><Label className="caption-label">Template de produção</Label>
                <Select value={form.template_id || "auto"} onValueChange={(valor) => setForm({ ...form, template_id: valor })}>
                  <SelectTrigger data-testid="fechamento-template" className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="auto">Automático pelo tipo de projeto</SelectItem>{templates.map((tpl) => <SelectItem key={tpl.id} value={tpl.id}>{tpl.nome || tpl.titulo || tpl.key}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {recorrente && <div><Label className="caption-label">Formato do contrato</Label>
                <Select value={form.formato} onValueChange={(valor) => setForm({ ...form, formato: valor })}>
                  <SelectTrigger className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="mensal">Mensal</SelectItem><SelectItem value="trimestral">Trimestral</SelectItem><SelectItem value="semestral">Semestral</SelectItem><SelectItem value="anual">Anual</SelectItem></SelectContent>
                </Select>
              </div>}
              <div><Label className="caption-label">Início da operação</Label><Input required type="date" data-testid="fechamento-inicio" value={form.inicio} onChange={(e) => setForm({ ...form, inicio: e.target.value })} className={campo} /></div>
              {recorrente && <div><Label className="caption-label">Fim do contrato (opcional)</Label><Input type="date" value={form.fim} onChange={(e) => setForm({ ...form, fim: e.target.value })} className={campo} /></div>}
            </div>
          </section>

          <section className="studio-card p-4 space-y-4">
            <div>
              <p className="caption-label text-[#10B981]">3. Pagamento e cobranças</p>
              <p className="text-xs text-[#71717A] mt-1">O valor cheio fica no contrato; o financeiro recebe somente as cobranças conforme o parcelamento.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">{recorrente ? "Mensalidade (R$)" : "Valor total vendido (R$)"}</Label><Input required min="0.01" step="0.01" type="number" data-testid="fechamento-valor" value={recorrente ? form.valor_mensal : form.valor_total} onChange={(e) => setForm({ ...form, [recorrente ? "valor_mensal" : "valor_total"]: e.target.value })} className={campo} /></div>
              {!recorrente && <div><Label className="caption-label">Condição de pagamento</Label>
                <Select value={form.pagamento_tipo} onValueChange={(valor) => setForm({
                  ...form,
                  pagamento_tipo: valor,
                  quantidade_parcelas: valor === "a_vista" ? 1 : Math.max(Number(form.quantidade_parcelas || 2), 2),
                  parcelas_personalizadas: valor === "personalizado" && !(form.parcelas_personalizadas || []).length
                    ? [{ data_vencimento: form.primeiro_vencimento || dataLocal(), valor: "" }]
                    : (form.parcelas_personalizadas || []),
                })}>
                  <SelectTrigger data-testid="fechamento-condicao" className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="a_vista">À vista</SelectItem><SelectItem value="parcelado">Parcelado</SelectItem><SelectItem value="personalizado">Cronograma personalizado</SelectItem></SelectContent>
                </Select>
              </div>}
              {!recorrente && parcelado && <div><Label className="caption-label">Quantidade de parcelas</Label><Input required min="2" max="120" type="number" data-testid="fechamento-parcelas" value={form.quantidade_parcelas} onChange={(e) => setForm({ ...form, quantidade_parcelas: e.target.value })} className={campo} /></div>}
              {!personalizado && <div><Label className="caption-label">Periodicidade</Label>
                <Select value={form.frequencia_cobranca} onValueChange={(valor) => setForm({ ...form, frequencia_cobranca: valor })}>
                  <SelectTrigger data-testid="fechamento-frequencia" className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="mensal">Mensal</SelectItem><SelectItem value="semanal">Semanal</SelectItem></SelectContent>
                </Select>
              </div>}
              {recorrente && form.frequencia_cobranca === "semanal" && <div><Label className="caption-label">Cobranças por mês</Label><Input min="1" max="5" type="number" value={form.parcelas_por_mes} onChange={(e) => setForm({ ...form, parcelas_por_mes: e.target.value })} className={campo} /><p className="text-[11px] text-[#71717A] mt-1">Use vencimento entre os dias 1 e 7 para distribuir todas no mesmo mês.</p></div>}
              {!personalizado && <div><Label className="caption-label">Primeiro vencimento</Label><Input required type="date" data-testid="fechamento-vencimento" value={form.primeiro_vencimento} onChange={(e) => setForm({ ...form, primeiro_vencimento: e.target.value })} className={campo} /></div>}
              <div><Label className="caption-label">Forma de pagamento</Label>
                <Select value={form.forma_pagamento} onValueChange={(valor) => setForm({ ...form, forma_pagamento: valor })}>
                  <SelectTrigger data-testid="fechamento-forma-pagamento" className={campo}><SelectValue /></SelectTrigger>
                  <SelectContent className={conteudoSelect}><SelectItem value="pix">PIX</SelectItem><SelectItem value="boleto">Boleto</SelectItem><SelectItem value="cartao">Cartão</SelectItem><SelectItem value="transferencia">Transferência</SelectItem><SelectItem value="dinheiro">Dinheiro</SelectItem><SelectItem value="outro">Outro</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="caption-label">Nota fiscal</Label>
                <Select value={form.nf} onValueChange={(valor) => setForm({ ...form, nf: valor })}>
                  <SelectTrigger className={campo}><SelectValue /></SelectTrigger><SelectContent className={conteudoSelect}><SelectItem value="netzer">NF Netzer</SelectItem><SelectItem value="nao">Sem NF</SelectItem><SelectItem value="sim">Com NF</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {personalizado && <div className="space-y-3 rounded-lg border border-[#10B981]/25 bg-[#10B981]/5 p-4" data-testid="cronograma-personalizado">
              <div className="flex items-center justify-between gap-3">
                <div><p className="caption-label text-[#10B981]">Cronograma personalizado</p><p className="mt-1 text-xs text-[#A1A1AA]">Informe a data e o valor de cada parcela. O total é calculado automaticamente.</p></div>
                <Button type="button" variant="outline" onClick={adicionarParcelaPersonalizada} className="border-[#10B981]/30 bg-transparent text-[#10B981]"><Plus size={14} className="mr-2" />Adicionar parcela</Button>
              </div>
              {(form.parcelas_personalizadas || []).map((parcela, indice) => <div key={indice} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Input aria-label={`Vencimento da parcela ${indice + 1}`} required type="date" value={parcela.data_vencimento || ""} onChange={(e) => atualizarParcelaPersonalizada(indice, "data_vencimento", e.target.value)} className={campo} />
                <Input aria-label={`Valor da parcela ${indice + 1}`} required min="0.01" step="0.01" type="number" value={parcela.valor ?? ""} onChange={(e) => atualizarParcelaPersonalizada(indice, "valor", e.target.value)} className={campo} />
                <Button type="button" variant="ghost" onClick={() => removerParcelaPersonalizada(indice)} disabled={(form.parcelas_personalizadas || []).length === 1} className="text-[#F43F5E]">Remover</Button>
              </div>)}
              <p className="text-right font-mono text-sm text-[#10B981]">Total: {BRL(Number(form.valor_total || 0))}</p>
            </div>}
            {recorrente && form.fim && <label className="flex items-center gap-2 text-sm text-[#A1A1AA]"><input type="checkbox" checked={form.renovacao_automatica} onChange={(e) => setForm({ ...form, renovacao_automatica: e.target.checked })} className="accent-[#8CC63F]" /> Renovar automaticamente ao final</label>}
          </section>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#3F3F46] bg-transparent">Cancelar</Button>
            <Button disabled={salvando} data-testid="fechamento-submit" type="submit" className="bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] font-semibold">{salvando ? "Criando estrutura…" : "Fechar negócio e criar estrutura"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Comercial() {
  const { user } = useAuth();
  const podeVer = user?.role === "admin" || user?.fin_access;
  const [ops, setOps] = useState([]);
  const [propostas, setPropostas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dialogOp, setDialogOp] = useState(false);
  const [form, setForm] = useState(emptyOp);
  const [dialogEditar, setDialogEditar] = useState(null);
  const [editForm, setEditForm] = useState(emptyOp);
  const [ganhoInfo, setGanhoInfo] = useState(null);
  const [equipe, setEquipe] = useState([]);
  const [busca, setBusca] = useState("");
  const [stageFiltro, setStageFiltro] = useState("todos");
  const [dialogProp, setDialogProp] = useState(null);
  const [propForm, setPropForm] = useState({ titulo: "", itens: [{ descricao: "", qtd: 1, valor: "" }], validade: "", observacoes: "" });
  const [fechamentoCtx, setFechamentoCtx] = useState(null);
  const [fechamentoForm, setFechamentoForm] = useState(null);
  const [salvandoFechamento, setSalvandoFechamento] = useState(false);

  const load = () => {
    api.get("/oportunidades").then((r) => setOps(r.data)).catch(() => {});
    if (podeVer) api.get("/propostas").then((r) => setPropostas(r.data)).catch(() => {});
    api.get("/equipe").then((r) => setEquipe(r.data)).catch(() => {});
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(load, [podeVer]);

  const propostasReais = useMemo(() => {
    const oportunidadesIds = new Set(ops.map((op) => op.id));
    return propostas.filter((proposta) => oportunidadesIds.has(proposta.oportunidade_id));
  }, [ops, propostas]);
  const contratoGanhoPontual = ganhoInfo?.contrato?.formato === "pontual";

  const abrirFechamento = async (op, proposta = null) => {
    const base = formularioFechamento(op, proposta);
    const clienteExistente = clientes.find((cliente) => cliente.id === op.cliente_id)
      || clientes.find((cliente) => cliente.empresa?.toLowerCase() === op.empresa?.toLowerCase());
    if (clienteExistente) {
      Object.assign(base, {
        cliente_id: clienteExistente.id,
        empresa: clienteExistente.empresa || base.empresa,
        contato: clienteExistente.nome || base.contato,
        email: clienteExistente.email || base.email,
        whatsapp: clienteExistente.whatsapp || base.whatsapp,
      });
    }
    setFechamentoCtx({ op, proposta });
    setFechamentoForm(base);
    if (op.stage !== "ganho" && !op.contrato_id) return;
    try {
      const { data } = await api.get(`/oportunidades/${op.id}/fechamento`);
      const contrato = data.contrato;
      const cliente = data.cliente;
      if (cliente) Object.assign(base, { cliente_id: cliente.id, empresa: cliente.empresa || "", contato: cliente.nome || "", email: cliente.email || "", whatsapp: cliente.whatsapp || "" });
      if (contrato) Object.assign(base, {
        tipo_contrato: contrato.formato === "pontual" ? "pontual" : "recorrente",
        formato: contrato.formato || "pontual",
        tipo_projeto: contrato.tipo_projeto || base.tipo_projeto,
        template_id: contrato.template_id || "auto",
        valor_total: contrato.valor_total || "",
        valor_mensal: contrato.valor_mensal || "",
        pagamento_tipo: (contrato.parcelas_personalizadas || []).length ? "personalizado" : (Number(contrato.quantidade_parcelas || 1) > 1 ? "parcelado" : "a_vista"),
        quantidade_parcelas: contrato.quantidade_parcelas || 1,
        frequencia_cobranca: contrato.frequencia_cobranca || "mensal",
        parcelas_por_mes: contrato.parcelas_por_mes || 1,
        primeiro_vencimento: (contrato.primeiro_vencimento || contrato.inicio || base.primeiro_vencimento).slice(0, 10),
        forma_pagamento: formaPagamentoValida(contrato.forma_pagamento),
        inicio: (contrato.inicio || base.inicio).slice(0, 10),
        fim: (contrato.fim || "").slice(0, 10),
        nf: contrato.nf || "nao",
        renovacao_automatica: Boolean(contrato.renovacao_automatica),
        frequencia_captacao: contrato.frequencia_captacao || "mensal",
        parcelas_personalizadas: (contrato.parcelas_personalizadas || []).map((parcela) => ({ ...parcela, valor: String(parcela.valor ?? "") })),
      });
      setFechamentoForm({ ...base });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resumoComercial = useMemo(() => {
    const abertas = ops.filter(oportunidadeAtiva);
    const limite30 = dataMaisDias(30);
    return abertas.reduce((resumo, oportunidade) => {
      const total = Number(oportunidade.valor || 0);
      const noForecast30 = oportunidade.data_fechamento && oportunidade.data_fechamento >= dataLocal() && oportunidade.data_fechamento <= limite30;
      if (noForecast30 && oportunidade.recorrente) {
        resumo.recorrencia30 += total;
        resumo.recorrencia30Qtd += 1;
      } else if (noForecast30) {
        resumo.pontuais30 += total;
        resumo.pontuais30Qtd += 1;
      }
      if (oportunidade.data_proxima_acao === dataLocal()) resumo.hoje += 1;
      if (oportunidade.data_proxima_acao && oportunidade.data_proxima_acao < dataLocal()) resumo.atrasadas += 1;
      if (!oportunidade.proxima_acao || !oportunidade.data_proxima_acao) resumo.semProximoPasso += 1;
      if ((diasDesde(dataDeMovimento(oportunidade)) ?? 0) > 7) resumo.paradas += 1;
      if (oportunidade.confianca_forecast === "comprometida") {
        if (oportunidade.recorrente) resumo.comprometidoRecorrente += total;
        else resumo.comprometidoPontual += total;
      }
      return resumo;
    }, {
      quantidade: abertas.length, pontuais30: 0, pontuais30Qtd: 0, recorrencia30: 0, recorrencia30Qtd: 0,
      hoje: 0, atrasadas: 0, semProximoPasso: 0, paradas: 0, comprometidoPontual: 0, comprometidoRecorrente: 0,
    });
  }, [ops]);

  const filaDeAcao = useMemo(() => ops
    .filter(oportunidadeAtiva)
    .filter((op) => !op.proxima_acao || !op.data_proxima_acao || op.data_proxima_acao <= dataLocal())
    .sort((a, b) => (a.data_proxima_acao || "0000-00-00").localeCompare(b.data_proxima_acao || "0000-00-00")), [ops]);

  if (!podeVer)
    return (
      <div className="studio-card p-12 text-center max-w-lg mx-auto mt-10" data-testid="comercial-restrito">
        <Lock size={32} className="text-[#8CC63F] mx-auto mb-5" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl text-[#F4F4F0]">Área <span className="gold-italic">restrita</span></h2>
        <p className="text-sm text-[#A1A1AA] mt-3">O pipeline comercial do estúdio é visível apenas para o administrador e perfis autorizados em <span className="text-[#E4E4E0]">Acessos</span>.</p>
      </div>
    );

  const salvarOp = async (e) => {
    e.preventDefault();
    if (!form.responsavel || !form.proxima_acao || !form.data_proxima_acao) {
      toast.error("Defina responsável, próxima ação e data antes de criar a oportunidade");
      return;
    }
    try {
      await api.post("/oportunidades", { ...form, valor: parseFloat(form.valor) || 0 });
      toast.success("Oportunidade criada");
      setDialogOp(false);
      setForm(emptyOp);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const abrirEdicao = (op, stage = null) => {
    setDialogEditar(op);
    setEditForm({
      ...emptyOp,
      ...op,
      stage: stage || etapaDaOportunidade(op),
      valor: op.valor ?? "",
    });
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    if (!dialogEditar) return;
    if (etapasAbertas.has(editForm.stage) && (!editForm.responsavel || !editForm.proxima_acao || !editForm.data_proxima_acao)) {
      toast.error("Oportunidades ativas precisam de responsável, próxima ação e data");
      return;
    }
    try {
      await api.patch(`/oportunidades/${dialogEditar.id}`, { ...editForm, valor: parseFloat(editForm.valor) || 0 });
      toast.success("Oportunidade atualizada");
      setDialogEditar(null);
      setEditForm(emptyOp);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const mover = async (op, stage) => {
    if (stage === "ganho") {
      abrirFechamento(op);
      return;
    }
    if (["nutricao", "perdido"].includes(stage)
      || (["diagnostico", "proposta", "decisao"].includes(stage)
        && (!op.responsavel || !op.proxima_acao || !op.data_proxima_acao))) {
      abrirEdicao(op, stage);
      toast.info("Complete os dados necessários para confirmar a mudança de etapa");
      return;
    }
    try {
      const res = await api.patch(`/oportunidades/${op.id}`, { stage });
      if (res.data.created) setGanhoInfo({ op: res.data.oportunidade, ...res.data.created });
      toast.success(stage === "ganho" ? "Negócio fechado!" : `Movido para ${stageInfo(stage).nome}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const excluir = async (op) => {
    if (!window.confirm(`Excluir “${op.titulo}” e todas as propostas vinculadas?`)) return;
    try {
      await api.delete(`/oportunidades/${op.id}`);
      toast.success("Oportunidade e propostas vinculadas removidas");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const salvarProposta = async (e) => {
    e.preventDefault();
    try {
      const itens = propForm.itens.filter((i) => i.descricao).map((i) => ({ ...i, valor: parseFloat(i.valor) || 0, qtd: parseInt(i.qtd) || 1 }));
      await api.post("/propostas", { ...propForm, itens, oportunidade_id: dialogProp.id });
      toast.success("Proposta gerada e enviada");
      setDialogProp(null);
      setPropForm({ titulo: "", itens: [{ descricao: "", qtd: 1, valor: "" }], validade: "", observacoes: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const aprovarProposta = async (p) => {
    const op = ops.find((item) => item.id === p.oportunidade_id);
    if (!op) {
      toast.error("A oportunidade vinculada a esta proposta não foi encontrada");
      return;
    }
    abrirFechamento(op, p);
  };

  const salvarFechamento = async (e) => {
    e.preventDefault();
    if (!fechamentoCtx || !fechamentoForm) return;
    setSalvandoFechamento(true);
    try {
      const payload = {
        ...fechamentoForm,
        cliente_id: fechamentoForm.cliente_id === "novo" ? "" : fechamentoForm.cliente_id,
        template_id: fechamentoForm.template_id === "auto" ? "" : fechamentoForm.template_id,
        valor_total: Number(fechamentoForm.valor_total || 0),
        valor_mensal: Number(fechamentoForm.valor_mensal || 0),
        quantidade_parcelas: fechamentoForm.pagamento_tipo === "a_vista" ? 1 : fechamentoForm.pagamento_tipo === "personalizado" ? (fechamentoForm.parcelas_personalizadas || []).length : Number(fechamentoForm.quantidade_parcelas || 1),
        parcelas_por_mes: Number(fechamentoForm.parcelas_por_mes || 1),
        parcelas_personalizadas: fechamentoForm.pagamento_tipo === "personalizado"
          ? (fechamentoForm.parcelas_personalizadas || []).map((parcela) => ({ ...parcela, valor: Number(parcela.valor || 0) }))
          : [],
      };
      const { data } = await api.post(`/oportunidades/${fechamentoCtx.op.id}/fechar`, payload);
      setGanhoInfo({ op: data.oportunidade, ...data.created });
      setFechamentoCtx(null);
      setFechamentoForm(null);
      toast.success("Contrato, cobranças e produção criados");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSalvandoFechamento(false);
    }
  };

  const excluirProposta = async (p) => {
    if (!window.confirm(`Excluir definitivamente a proposta “${p.titulo}”?`)) return;
    try {
      await api.delete(`/propostas/${p.id}`);
      toast.success("Proposta removida do Comercial");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const renderOportunidade = (op) => {
    const situacao = situacaoDaOportunidade(op);
    const confianca = confiancaForecast[op.confianca_forecast] || confiancaForecast.aberta;
    const paradaHa = diasDesde(dataDeMovimento(op));
    const etapaAtual = etapaDaOportunidade(op);
    const ativa = oportunidadeAtiva(op);
    const acaoExibida = ativa
      ? op.proxima_acao
      : etapaAtual === "nutricao" ? (op.proxima_acao || "Retomar relacionamento")
        : etapaAtual === "perdido" ? op.motivo_perda
          : "Fechamento registrado";
    const dataAcaoExibida = ativa ? op.data_proxima_acao
      : etapaAtual === "nutricao" ? op.data_retomada
        : (op.closed_at || op.lost_at || "").slice(0, 10);
    return (
      <motion.div key={op.id} layout data-testid={`oportunidade-card-${op.id}`} className="studio-card studio-card-hover p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#E4E4E0] leading-snug">{op.titulo}</p>
            <p className="caption-label mt-1 truncate">{op.empresa}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`oportunidade-menu-${op.id}`} className="text-[#71717A] hover:text-[#8CC63F] shrink-0"><MoreHorizontal size={16} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
              <DropdownMenuItem data-testid={`editar-${op.id}`} onClick={() => abrirEdicao(op)} className="cursor-pointer focus:bg-[#8CC63F]/10 focus:text-[#8CC63F]">
                <Pencil size={14} className="mr-2" /> Editar e registrar avanço
              </DropdownMenuItem>
              {STAGES.filter((stage) => stage.key !== etapaAtual).map((stage) => (
                <DropdownMenuItem key={stage.key} data-testid={`mover-${op.id}-${stage.key}`} onClick={() => mover(op, stage.key)} className="cursor-pointer focus:bg-[#8CC63F]/10 focus:text-[#8CC63F]">
                  Mover: {stage.nome}
                </DropdownMenuItem>
              ))}
              {podeVer && !["ganho", "perdido"].includes(etapaAtual) && (
                <DropdownMenuItem data-testid={`proposta-${op.id}`} onClick={() => { setDialogProp(op); setPropForm((f) => ({ ...f, titulo: `Proposta — ${op.titulo}` })); }} className="cursor-pointer focus:bg-[#8CC63F]/10 focus:text-[#8CC63F]">
                  <FileText size={14} className="mr-2" /> Gerar proposta
                </DropdownMenuItem>
              )}
              {etapaAtual === "ganho" && (
                <DropdownMenuItem data-testid={`fechamento-${op.id}`} onClick={() => abrirFechamento(op)} className="cursor-pointer focus:bg-[#8CC63F]/10 focus:text-[#8CC63F]">
                  <CheckCircle2 size={14} className="mr-2" /> Revisar fechamento
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid={`excluir-${op.id}`} onClick={() => excluir(op)} className="cursor-pointer text-[#F43F5E] focus:bg-[#F43F5E]/10">
                <Trash2 size={14} className="mr-2" /> Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-1 rounded-full border ${situacao.classe}`}>{situacao.label}</span>
          {ativa && <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-1 rounded-full border ${confianca.classe}`}>{confianca.label}</span>}
        </div>
        <div className="mt-4 border-t border-[#27272A] pt-3 min-h-[58px]">
          <p className="font-mono text-[9px] uppercase tracking-wider text-[#71717A]">{ativa ? "Próxima ação" : etapaAtual === "nutricao" ? "Retomada" : "Resultado"}</p>
          <p className={`text-xs mt-1 ${acaoExibida ? "text-[#E4E4E0]" : "text-[#F43F5E]"}`}>{acaoExibida || "Registro incompleto"}</p>
          <p className={`font-mono text-[10px] mt-1 ${ativa && dataAcaoExibida && dataAcaoExibida < dataLocal() ? "text-[#F43F5E]" : "text-[#71717A]"}`}>{dataAcaoExibida ? fmtData(dataAcaoExibida) : "Sem data"}{op.responsavel ? ` · ${op.responsavel}` : " · Sem responsável"}</p>
        </div>
        <div className="flex items-end justify-between gap-3 mt-3">
          <div>
            <span className="font-mono text-sm text-[#8CC63F]">{podeVer && op.valor != null ? BRL(op.valor) : "R$ •••"}</span>
            {op.recorrente && <span className="font-mono text-[9px] text-[#06B6D4] uppercase ml-1">/mês</span>}
          </div>
          {paradaHa != null && paradaHa > 7 && ativa && <span className="font-mono text-[9px] text-[#F59E0B]">{paradaHa}d sem avanço</span>}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6" data-testid="comercial-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="caption-label mb-2">03 // Comercial</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight">Pipeline & <span className="gold-italic">Propostas</span></h1>
        </div>
        <Button data-testid="nova-oportunidade-button" onClick={() => setDialogOp(true)} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold sm:w-auto">
          <Plus size={16} className="mr-1" /> Nova oportunidade
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="comercial-resumo">
        <div className="studio-card p-5" data-testid="comercial-resumo-acoes">
          <p className="caption-label">Ação necessária · {resumoComercial.quantidade} ativas</p>
          <p className="font-mono text-3xl text-[#F4F4F0] mt-3">{resumoComercial.hoje + resumoComercial.atrasadas}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[#71717A] mt-2">{resumoComercial.atrasadas} atrasadas · {resumoComercial.hoje} para hoje</p>
        </div>
        <div className="studio-card p-5" data-testid="comercial-resumo-pontuais">
          <p className="caption-label">Pontual · próximos 30 dias</p>
          <p className="font-mono text-2xl text-[#8CC63F] mt-3">{BRL(resumoComercial.pontuais30)}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[#71717A] mt-2">{resumoComercial.pontuais30Qtd} oportunidades com previsão</p>
        </div>
        <div className="studio-card p-5" data-testid="comercial-resumo-recorrencia">
          <p className="caption-label">Novo MRR · próximos 30 dias</p>
          <p className="font-mono text-2xl text-[#06B6D4] mt-3">{BRL(resumoComercial.recorrencia30)}<span className="text-sm text-[#71717A]">/mês</span></p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[#71717A] mt-2">{resumoComercial.recorrencia30Qtd} oportunidades com previsão</p>
        </div>
        <div className="studio-card p-5" data-testid="comercial-resumo-comprometido">
          <p className="caption-label">Forecast comprometido</p>
          <p className="font-mono text-lg text-[#8CC63F] mt-3">{BRL(resumoComercial.comprometidoPontual)}</p>
          <p className="font-mono text-xs text-[#06B6D4] mt-1">+ {BRL(resumoComercial.comprometidoRecorrente)}/mês</p>
        </div>
        <div className="studio-card p-5" data-testid="comercial-resumo-risco">
          <p className="caption-label">Pipeline em risco</p>
          <p className="font-mono text-3xl text-[#F59E0B] mt-3">{resumoComercial.semProximoPasso}</p>
          <p className="font-mono text-[10px] uppercase tracking-wider text-[#71717A] mt-2">sem próximo passo · {resumoComercial.paradas} paradas há 8d+</p>
        </div>
      </div>

      {resumoComercial.semProximoPasso > 0 && (
        <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-4 py-3 flex items-start gap-3" data-testid="comercial-cadastros-pendentes">
          <AlertTriangle size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#F4F4F0]">{resumoComercial.semProximoPasso} oportunidade(s) estão abertas sem próximo passo completo.</p>
            <p className="text-xs text-[#A1A1AA] mt-1">Defina ação, data e responsável. Sem isso, o negócio não entra em uma cadência comercial confiável.</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="acoes">
        <TabsList className="w-full h-auto flex flex-wrap border border-[#27272A] bg-[#141418]">
          <TabsTrigger value="acoes" data-testid="tab-acoes">Ações ({filaDeAcao.length})</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
          <TabsTrigger value="tabela" data-testid="tab-tabela">Tabela</TabsTrigger>
          {podeVer && <TabsTrigger value="propostas" data-testid="tab-propostas">Histórico de propostas ({propostasReais.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="acoes" className="mt-6">
          <div className="studio-card overflow-hidden">
            <div className="px-5 py-4 border-b border-[#27272A]">
              <p className="text-sm font-medium text-[#F4F4F0]">Fila comercial</p>
              <p className="text-xs text-[#71717A] mt-1">Atrasos, compromissos de hoje e oportunidades ainda sem próximo passo.</p>
            </div>
            {filaDeAcao.length === 0 ? (
              <p className="text-sm text-[#71717A] px-5 py-8">Nenhuma ação vencida ou pendente para hoje.</p>
            ) : filaDeAcao.map((op) => {
              const situacao = situacaoDaOportunidade(op);
              return (
                <button type="button" key={op.id} onClick={() => abrirEdicao(op)} className="w-full text-left px-5 py-4 border-b border-[#27272A]/70 hover:bg-[#18181C] transition-colors" data-testid={`acao-${op.id}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-[#E4E4E0]">{op.empresa}</p>
                        <span className="font-mono text-[9px] uppercase tracking-wider text-[#8CC63F]">{stageInfo(op.stage).nome}</span>
                        <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${situacao.classe}`}>{situacao.label}</span>
                      </div>
                      <p className={`text-xs mt-2 ${op.proxima_acao ? "text-[#A1A1AA]" : "text-[#F43F5E]"}`}>{op.proxima_acao || "Definir próximo passo"}</p>
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <p className="font-mono text-xs text-[#E4E4E0]">{op.data_proxima_acao ? fmtData(op.data_proxima_acao) : "Sem data"}</p>
                      <p className="font-mono text-[10px] text-[#71717A] mt-1">{op.responsavel || "Sem responsável"}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {PIPELINE_STAGES.map((s) => {
              const cards = ops.filter((o) => etapaDaOportunidade(o) === s.key);
              return (
                <div key={s.key} data-testid={`comercial-kanban-column-${s.key}`} className="min-w-0">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <span className="caption-label">{s.nome}</span>
                    <span className="font-mono text-xs text-[#8CC63F]">{cards.length}</span>
                  </div>
                  <div className="space-y-3 min-h-[120px] rounded-xl border border-dashed border-[#27272A] p-2">
                    {cards.map(renderOportunidade)}
                    {cards.length === 0 && <p className="text-xs text-[#52525B] px-2 py-4">Nenhuma oportunidade nesta etapa.</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="historico" className="mt-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {HISTORY_STAGES.map((stage) => {
              const cards = ops.filter((op) => etapaDaOportunidade(op) === stage.key);
              return (
                <section key={stage.key} className="space-y-3" data-testid={`historico-${stage.key}`}>
                  <div className="flex items-center justify-between px-1">
                    <span className="caption-label">{stage.nome}</span>
                    <span className="font-mono text-xs text-[#71717A]">{cards.length}</span>
                  </div>
                  <div className="space-y-3">{cards.map(renderOportunidade)}</div>
                  {cards.length === 0 && <div className="rounded-xl border border-dashed border-[#27272A] p-5 text-xs text-[#52525B]">Nenhum registro.</div>}
                </section>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="tabela" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-3">
            <Input data-testid="tabela-busca" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar empresa, contato, serviço…" className="w-full bg-[#141418] border-[#27272A] sm:w-72" />
            <Select value={stageFiltro} onValueChange={setStageFiltro}>
              <SelectTrigger data-testid="tabela-stage-filtro" className="w-full bg-[#141418] border-[#27272A] sm:w-48"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                <SelectItem value="todos">Todas as etapas</SelectItem>
                {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="studio-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="oportunidades-tabela">
              <thead>
                <tr className="border-b border-[#27272A]">
                  {["Empresa", "Próxima ação", "Data", "Etapa", "Confiança", "Fechamento", "Responsável", "Valor"].map((h) => (
                    <th key={h} className="caption-label text-left px-4 py-3 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ops
                  .filter((o) => stageFiltro === "todos" || etapaDaOportunidade(o) === stageFiltro)
                  .filter((o) => !busca || `${o.empresa} ${o.contato} ${o.servico}`.toLowerCase().includes(busca.toLowerCase()))
                  .map((o) => (
                    <tr key={o.id} onClick={() => abrirEdicao(o)} data-testid={`tabela-row-${o.id}`} className="border-b border-[#27272A]/60 hover:bg-[#18181C] transition-colors cursor-pointer">
                      <td className="px-4 py-3 text-[#E4E4E0]">{o.empresa}</td>
                      <td className="px-4 py-3 text-[#A1A1AA] min-w-[220px]">{o.proxima_acao || <span className="text-[#F43F5E]">Definir próximo passo</span>}</td>
                      <td className={`px-4 py-3 font-mono text-xs ${o.data_proxima_acao && o.data_proxima_acao < dataLocal() ? "text-[#F43F5E]" : "text-[#71717A]"}`}>{o.data_proxima_acao ? fmtData(o.data_proxima_acao) : "—"}</td>
                      <td className="px-4 py-3"><span className="font-mono text-[10px] uppercase tracking-widest text-[#8CC63F]">{stageInfo(o.stage).nome}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-[#71717A]">{(confiancaForecast[o.confianca_forecast] || confiancaForecast.aberta).label}</td>
                      <td className={`px-4 py-3 font-mono text-xs ${o.data_fechamento && oportunidadeAtiva(o) && o.data_fechamento < dataLocal() ? "text-[#F43F5E]" : "text-[#71717A]"}`}>{o.data_fechamento ? fmtData(o.data_fechamento) : "—"}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{o.responsavel || "—"}</td>
                      <td className="px-4 py-3 font-mono text-[#8CC63F]">{podeVer && o.valor != null ? BRL(o.valor) : "R$ •••"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {ops.filter((o) => stageFiltro === "todos" || etapaDaOportunidade(o) === stageFiltro).length === 0 && <p className="text-sm text-[#71717A] px-4 py-6">Nenhuma oportunidade nesta etapa.</p>}
          </div>
        </TabsContent>

        <TabsContent value="propostas" className="mt-6 space-y-3">
          {propostasReais.length === 0 && <p className="text-sm text-[#71717A]">Nenhuma proposta gerada ainda. Abra uma oportunidade e clique em “Gerar proposta”.</p>}
          {propostasReais.map((p) => {
            const status = p.status_efetivo || p.status;
            const visual = statusProposta[status] || statusProposta.enviada;
            return (
            <div key={p.id} data-testid={`proposta-card-${p.id}`} className="studio-card p-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="font-mono text-xs text-[#71717A]">{p.numero}</p>
                <p className="text-sm font-medium text-[#E4E4E0] mt-1">{p.titulo}</p>
              </div>
              <span className="font-mono text-lg text-[#8CC63F]">{BRL(p.total)}</span>
              <span className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${visual.classe}`} data-testid={`proposta-status-${p.id}`}>{visual.label}</span>
              {status === "enviada" && (
                <Button size="sm" data-testid={`aprovar-proposta-${p.id}`} onClick={() => aprovarProposta(p)} className="bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 hover:bg-[#10B981]/25 rounded-full">
                  <CheckCircle2 size={14} className="mr-1" /> Aprovar
                </Button>
              )}
              <button type="button" data-testid={`excluir-proposta-${p.id}`} onClick={() => excluirProposta(p)} title="Excluir proposta" aria-label={`Excluir proposta ${p.titulo}`} className="p-2 text-[#71717A] hover:text-[#F43F5E] transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Registro e avanço da oportunidade */}
      <Dialog open={!!dialogEditar} onOpenChange={(aberto) => { if (!aberto) setDialogEditar(null); }}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Registro <span className="gold-italic">comercial</span></DialogTitle></DialogHeader>
          <p className="text-xs text-[#71717A]">A etapa mostra onde a decisão está. A próxima ação mostra o que realmente fará o negócio avançar.</p>
          <form onSubmit={salvarEdicao} className="space-y-6 mt-2" data-testid="oportunidade-edicao-form">
            <section className="studio-card p-4 space-y-4">
              <p className="caption-label text-[#8CC63F]">Situação da venda</p>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><Label className="caption-label">Etapa</Label>
                  <Select disabled={etapaDaOportunidade(dialogEditar || {}) === "ganho"} value={editForm.stage} onValueChange={(valor) => setEditForm({ ...editForm, stage: valor })}>
                    <SelectTrigger data-testid="edit-stage-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">{STAGES.filter((stage) => stage.key !== "ganho" || editForm.stage === "ganho").map((stage) => <SelectItem key={stage.key} value={stage.key}>{stage.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="caption-label">Situação</Label>
                  <Select value={editForm.situacao} onValueChange={(valor) => setEditForm({ ...editForm, situacao: valor })}>
                    <SelectTrigger data-testid="edit-situacao-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]"><SelectItem value="em_andamento">Em andamento</SelectItem><SelectItem value="aguardando_cliente">Aguardando cliente</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="caption-label">Confiança do forecast</Label>
                  <Select value={editForm.confianca_forecast} onValueChange={(valor) => setEditForm({ ...editForm, confianca_forecast: valor })}>
                    <SelectTrigger data-testid="edit-confianca-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]"><SelectItem value="aberta">Aberta</SelectItem><SelectItem value="provavel">Provável</SelectItem><SelectItem value="comprometida">Comprometida</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section className="studio-card p-4 space-y-4">
              <p className="caption-label text-[#06B6D4]">Oportunidade</p>
              <div><Label className="caption-label">Título</Label><Input required value={editForm.titulo} onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Empresa</Label><Input required value={editForm.empresa} onChange={(e) => setEditForm({ ...editForm, empresa: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Serviço</Label><Input value={editForm.servico} onChange={(e) => setEditForm({ ...editForm, servico: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Contato</Label><Input value={editForm.contato} onChange={(e) => setEditForm({ ...editForm, contato: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Decisor</Label><Input value={editForm.decisor} onChange={(e) => setEditForm({ ...editForm, decisor: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Valor estimado (R$)</Label><Input type="number" min="0" step="0.01" value={editForm.valor} onChange={(e) => setEditForm({ ...editForm, valor: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Previsão de fechamento</Label><Input type="date" value={editForm.data_fechamento} onChange={(e) => setEditForm({ ...editForm, data_fechamento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA]"><input type="checkbox" checked={editForm.recorrente} onChange={(e) => setEditForm({ ...editForm, recorrente: e.target.checked })} className="accent-[#8CC63F]" />Contrato recorrente — o valor será tratado como mensalidade</label>
              <div><Label className="caption-label">Necessidade / objetivo comprovado</Label><Textarea value={editForm.necessidade} onChange={(e) => setEditForm({ ...editForm, necessidade: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={2} /></div>
            </section>

            <section className="studio-card p-4 space-y-4">
              <p className="caption-label text-[#F59E0B]">Cadência e evidência</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Responsável</Label>
                  <Select value={editForm.responsavel || "none"} onValueChange={(valor) => setEditForm({ ...editForm, responsavel: valor === "none" ? "" : valor })}>
                    <SelectTrigger data-testid="edit-responsavel-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]"><SelectItem value="none">Sem responsável</SelectItem>{equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="caption-label">Data da próxima ação</Label><Input required={etapasAbertas.has(editForm.stage)} type="date" value={editForm.data_proxima_acao} onChange={(e) => setEditForm({ ...editForm, data_proxima_acao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              </div>
              <div><Label className="caption-label">Próxima ação concreta</Label><Input required={etapasAbertas.has(editForm.stage)} value={editForm.proxima_acao} onChange={(e) => setEditForm({ ...editForm, proxima_acao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: ligar para o decisor e confirmar aprovação" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="caption-label">Última interação</Label><Input type="date" value={(editForm.ultima_interacao || "").slice(0, 10)} onChange={(e) => setEditForm({ ...editForm, ultima_interacao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
                <div><Label className="caption-label">Evidência do avanço</Label><Input value={editForm.evidencia} onChange={(e) => setEditForm({ ...editForm, evidencia: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: proposta recebida; decisão prometida para sexta" /></div>
              </div>
              {editForm.stage === "nutricao" && <div><Label className="caption-label">Data de retomada</Label><Input required type="date" value={editForm.data_retomada} onChange={(e) => setEditForm({ ...editForm, data_retomada: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>}
              {editForm.stage === "perdido" && <div><Label className="caption-label">Motivo da perda</Label><Textarea required value={editForm.motivo_perda} onChange={(e) => setEditForm({ ...editForm, motivo_perda: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={2} /></div>}
              <div><Label className="caption-label">Notas</Label><Textarea value={editForm.notas} onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={3} /></div>
            </section>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogEditar(null)} className="border-[#3F3F46] bg-transparent">Cancelar</Button>
              <Button data-testid="edit-submit-button" type="submit" className="bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] font-semibold">Salvar registro comercial</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nova oportunidade */}
      <Dialog open={dialogOp} onOpenChange={setDialogOp}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nova <span className="gold-italic">oportunidade</span></DialogTitle></DialogHeader>
          <form onSubmit={salvarOp} className="space-y-4 mt-2">
            <div><Label className="caption-label">Título do projeto</Label>
              <Input data-testid="op-titulo-input" required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: Conteúdo mensal — restaurante" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Empresa</Label>
                <Input data-testid="op-empresa-input" required value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Contato</Label>
                <Input data-testid="op-contato-input" value={form.contato} onChange={(e) => setForm({ ...form, contato: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Valor estimado (R$)</Label>
                <Input data-testid="op-valor-input" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Origem</Label>
                <Input data-testid="op-origem-input" value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Instagram, indicação…" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Serviço de interesse</Label>
                <Input data-testid="op-servico-input" value={form.servico} onChange={(e) => setForm({ ...form, servico: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: Conteúdo mensal, institucional…" /></div>
              <div><Label className="caption-label">Previsão de fechamento</Label>
                <Input data-testid="op-fechamento-input" type="date" value={form.data_fechamento} onChange={(e) => setForm({ ...form, data_fechamento: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Responsável</Label>
                <Select value={form.responsavel || "none"} onValueChange={(v) => setForm({ ...form, responsavel: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="op-responsavel-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="none">—</SelectItem>
                    {equipe.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="caption-label">Data da próxima ação</Label>
                <Input data-testid="op-proxima-acao-data" required type="date" value={form.data_proxima_acao} onChange={(e) => setForm({ ...form, data_proxima_acao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            <div><Label className="caption-label">Próxima ação concreta</Label>
              <Input data-testid="op-proxima-acao" required value={form.proxima_acao} onChange={(e) => setForm({ ...form, proxima_acao: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: ligar para confirmar reunião de diagnóstico" /></div>
            <div><Label className="caption-label">WhatsApp</Label>
              <Input data-testid="op-whatsapp-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <input type="checkbox" data-testid="op-recorrente-check" checked={form.recorrente} onChange={(e) => setForm({ ...form, recorrente: e.target.checked })} className="accent-[#8CC63F]" />
              Contrato recorrente (mensalidade)
            </label>
            <Button data-testid="op-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Adicionar ao pipeline</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gerar proposta */}
      <Dialog open={!!dialogProp} onOpenChange={() => setDialogProp(null)}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Proposta <span className="gold-italic">comercial</span></DialogTitle></DialogHeader>
          <form onSubmit={salvarProposta} className="space-y-4 mt-2">
            <div><Label className="caption-label">Título</Label>
              <Input data-testid="prop-titulo-input" required value={propForm.titulo} onChange={(e) => setPropForm({ ...propForm, titulo: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            {propForm.itens.map((item, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_64px_96px]">
                <Input data-testid={`prop-item-desc-${i}`} placeholder="Descrição do item" value={item.descricao} onChange={(e) => { const it = [...propForm.itens]; it[i].descricao = e.target.value; setPropForm({ ...propForm, itens: it }); }} className="bg-[#18181C] border-[#27272A]" />
                <Input data-testid={`prop-item-qtd-${i}`} type="number" min="1" value={item.qtd} onChange={(e) => { const it = [...propForm.itens]; it[i].qtd = e.target.value; setPropForm({ ...propForm, itens: it }); }} className="bg-[#18181C] border-[#27272A]" />
                <Input data-testid={`prop-item-valor-${i}`} type="number" step="0.01" placeholder="R$" value={item.valor} onChange={(e) => { const it = [...propForm.itens]; it[i].valor = e.target.value; setPropForm({ ...propForm, itens: it }); }} className="bg-[#18181C] border-[#27272A]" />
              </div>
            ))}
            <button type="button" data-testid="prop-add-item" onClick={() => setPropForm({ ...propForm, itens: [...propForm.itens, { descricao: "", qtd: 1, valor: "" }] })} className="text-xs font-mono text-[#8CC63F] hover:text-[#A9DD6B]">+ adicionar item</button>
            <div><Label className="caption-label">Observações</Label>
              <Textarea data-testid="prop-obs-input" value={propForm.observacoes} onChange={(e) => setPropForm({ ...propForm, observacoes: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={2} /></div>
            <Button data-testid="prop-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Gerar e enviar proposta</Button>
          </form>
        </DialogContent>
      </Dialog>

      <FechamentoDialog
        contexto={fechamentoCtx}
        form={fechamentoForm}
        setForm={setFechamentoForm}
        clientes={clientes}
        templates={templates}
        salvando={salvandoFechamento}
        onClose={() => { setFechamentoCtx(null); setFechamentoForm(null); }}
        onSubmit={salvarFechamento}
      />

      {/* Automação de ganho */}
      <Dialog open={!!ganhoInfo} onOpenChange={() => setGanhoInfo(null)}>
        <DialogContent className="bg-[#141418] border-[#8CC63F]/40 text-[#F4F4F0] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Negócio <span className="gold-italic">fechado!</span></DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 text-sm">
            <p className="text-[#A1A1AA]">Automação executada para <span className="text-[#F4F4F0]">{ganhoInfo?.op?.titulo}</span>:</p>
            {ganhoInfo?.cliente && (
              <div className="border border-[#10B981]/30 bg-[#10B981]/10 rounded-lg px-4 py-3" data-testid="automacao-cliente">
                <p className="caption-label text-[#10B981]">{ganhoInfo.cliente_criado ? "Cliente criado no CRM" : "Cliente vinculado no CRM"}</p>
                <p className="text-[#E4E4E0] mt-1">{ganhoInfo.cliente.empresa}</p>
              </div>
            )}
            {ganhoInfo?.transacao && (
              <div className="border border-[#8CC63F]/30 bg-[#8CC63F]/10 rounded-lg px-4 py-3" data-testid="automacao-receita">
                <p className="caption-label text-[#8CC63F]">Cobranças criadas no financeiro</p>
                <p className="font-mono text-lg text-[#F4F4F0] mt-1">{ganhoInfo.transacoes?.length || 1} cobrança(s) · {BRL(ganhoInfo.contrato?.formato === "pontual" ? ganhoInfo.contrato?.valor_total : ganhoInfo.transacao.valor)}</p>
              </div>
            )}
            {ganhoInfo?.contrato && (
              <div className="border border-[#06B6D4]/30 bg-[#06B6D4]/10 rounded-lg px-4 py-3" data-testid="automacao-contrato">
                <p className="caption-label text-[#06B6D4]">{contratoGanhoPontual ? "Projeto pontual criado" : "Contrato recorrente criado"}</p>
                <p className="text-[#E4E4E0] mt-1">{BRL(contratoGanhoPontual ? ganhoInfo.contrato.valor_total : ganhoInfo.contrato.valor_mensal)}{contratoGanhoPontual ? "" : "/mês"}</p>
              </div>
            )}
            {ganhoInfo?.onboarding && (
              <div className="border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 rounded-lg px-4 py-3" data-testid="automacao-onboarding">
                <p className="caption-label text-[#8B5CF6]">Onboarding iniciado</p>
                <p className="text-[#E4E4E0] mt-1">{ganhoInfo.onboarding.passos.length} passos — da emissão do contrato à reunião de alinhamento</p>
              </div>
            )}
            {ganhoInfo?.ciclo && (
              <div className="border border-[#EC4899]/30 bg-[#EC4899]/10 rounded-lg px-4 py-3" data-testid="automacao-ciclo">
                <p className="caption-label text-[#EC4899]">Primeiro ciclo de produção criado</p>
                <p className="text-[#E4E4E0] mt-1">{ganhoInfo.ciclo.titulo}</p>
              </div>
            )}
            <Button data-testid="automacao-close-button" onClick={() => setGanhoInfo(null)} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold mt-2">Continuar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
